import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";

const empty = { title: "", slug: "", excerpt: "", content: "", author: "HTP Sports Hub", cover_image: "", category: "Training", tags: "", published: true };

export default function AdminBlogs() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/blogs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({}); setForm(empty); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...b, tags: (b.tags || []).join(", ") });
  };
  const close = () => setEditing(null);

  const save = async () => {
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      };
      if (editing?.id) payload.id = editing.id;
      await api.post("/admin/blogs", payload);
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/admin/blogs/${id}`);
    load();
  };

  return (
    <div data-testid="admin-blogs">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// CMS · Blog Editor</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Blogs.</h1>
        </div>
        <button onClick={openNew} data-testid="new-blog-btn" className="bg-brand-blue text-white px-5 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2">
          <Plus size={12} /> New Blog
        </button>
      </div>

      <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12">
              <th className="text-left p-4">Title</th>
              <th className="text-left p-4">Category</th>
              <th className="text-left p-4">Slug</th>
              <th className="text-left p-4">Published</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-b border-white/5" data-testid={`admin-blog-${b.slug}`}>
                <td className="p-4">{b.title}</td>
                <td className="p-4 text-white/60">{b.category}</td>
                <td className="p-4 font-mono text-xs text-white/50">/{b.slug}</td>
                <td className="p-4">{b.published ? <span className="brand-blue">●</span> : <span className="text-white/30">○</span>}</td>
                <td className="p-4 text-right space-x-2">
                  <button onClick={() => openEdit(b)} data-testid={`edit-blog-${b.slug}`} className="text-white/60 hover:text-brand-orange"><Edit3 size={14} /></button>
                  <button onClick={() => del(b.id)} data-testid={`delete-blog-${b.slug}`} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl uppercase font-black">{editing.id ? "Edit" : "New"} Blog</h3>
              <button onClick={close}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} placeholder="Title" data-testid="blog-title" className="w-full bg-transparent border border-white/20 py-3 px-4" />
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="slug-url" data-testid="blog-slug" className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Cover image</div>
                <ImageUploader
                  value={form.cover_image}
                  onUploaded={(url) => setForm({ ...form, cover_image: url })}
                  onCleared={() => setForm({ ...form, cover_image: "" })}
                  label="Upload cover image"
                  testid="blog-cover-uploader"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Author" className="bg-transparent border border-white/20 py-3 px-4" />
                <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" className="bg-transparent border border-white/20 py-3 px-4" />
              </div>
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma separated)" className="w-full bg-transparent border border-white/20 py-3 px-4" />
              <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Excerpt" rows={2} className="w-full bg-transparent border border-white/20 py-3 px-4" />
              <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Content (Markdown / plain text)" rows={10} data-testid="blog-content" className="w-full bg-transparent border border-white/20 py-3 px-4" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
              </label>
            </div>
            <button onClick={save} data-testid="blog-save" className="mt-6 w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest">Save Blog</button>
          </div>
        </div>
      )}
    </div>
  );
}

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60);
}
